import { useRef, useState } from "react";
import {
  ImagePlus,
  Layers,
  Plus,
  Trash2,
  Maximize,
  Save,
  Type,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import type { Design, Layer, Media } from "./types";
import { templates } from "./types";
import { Wall } from "./Wall";
import { upload, save } from "./api";
export function DesignEditor({
  name,
  design,
  onChange,
  photos,
  assets,
  eventId,
  onAsset,
  notify,
  customTemplates,
}: {
  name: string;
  design: Design;
  onChange: (d: Design) => void;
  photos: Media[];
  assets: Media[];
  eventId?: string;
  onAsset: (m: Media) => void;
  notify: (message: string) => void;
  customTemplates: { id: string; name: string; design: Design }[];
}) {
  const [tab, setTab] = useState("canvas"),
    [selected, setSelected] = useState(""),
    [busy, setBusy] = useState(false);
  const preview = useRef<HTMLDivElement>(null),
    file = useRef<HTMLInputElement>(null);
  const target = useRef<"background" | "layer">("background");
  const patch = (value: Partial<Design>) => onChange({ ...design, ...value });
  const layer = design.layers.find((l) => l.id === selected);
  const patchLayer = (id: string, value: Partial<Layer>) =>
    patch({
      layers: design.layers.map((l) => (l.id === id ? { ...l, ...value } : l)),
    });
  function add(type: "text" | "image", assetId = "") {
    const id = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");
    patch({
      layers: [
        ...design.layers,
        {
          id,
          type,
          text: "YOUR EVENT",
          assetId,
          x: 8,
          y: 8,
          width: 30,
          height: 12,
          rotation: 0,
          opacity: 1,
          z: 10 + design.layers.length,
          visible: true,
          color: "#ffffff",
          fontSize: 48,
        },
      ],
    });
    setSelected(id);
    setTab("layers");
  }
  async function uploadAsset(f: File) {
    if (!eventId) {
      notify("Create the event first, then upload background and logo files.");
      return;
    }
    setBusy(true);
    try {
      const m: Media = await upload(eventId, f, "asset");
      onAsset(m);
      if (target.current === "background") patch({ backgroundAsset: m.id });
      else add("image", m.id);
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const number = (
    label: string,
    key: keyof Design,
    min: number,
    max: number,
  ) => (
    <label>
      {label}
      <input
        type="number"
        min={min}
        max={max}
        value={Number(
          design[key] ??
            (
              {
                backgroundX: 50,
                backgroundY: 50,
                backgroundScale: 100,
                motionBlur: 4,
              } as Record<string, number>
            )[key as string] ??
            0,
        )}
        onChange={(e) =>
          patch({ [key]: Math.max(min, Math.min(max, Number(e.target.value))) })
        }
      />
    </label>
  );
  const layerNumber = (
    label: string,
    key: keyof Layer,
    min: number,
    max: number,
    step = 1,
  ) =>
    layer && (
      <label>
        {label}
        <input
          type="number"
          step={step}
          min={min}
          max={max}
          value={Number(layer[key])}
          onChange={(e) =>
            patchLayer(layer.id, {
              [key]: Math.max(min, Math.min(max, Number(e.target.value))),
            })
          }
        />
      </label>
    );
  return (
    <div className="designer">
      <div className="design-workspace">
        <div className="preview-toolbar">
          <span>
            <span className="live-dot" /> LIVE PREVIEW
          </span>
          <span>
            {design.width} × {design.height}{" "}
            <button
              title="Fullscreen preview"
              onClick={() => void preview.current?.requestFullscreen()}
            >
              <Maximize size={16} />
            </button>
          </span>
        </div>
        <div className="preview-stage" ref={preview}>
          <Wall
            name={name}
            design={design}
            photos={photos}
            assets={assets}
            selected={selected}
            onSelect={setSelected}
            onMove={(id, x, y) => patchLayer(id, { x, y })}
          />
        </div>
        <div className="preview-caption">
          <span>
            Drag a graphic to position it. Your LED wall uses this exact design.
          </span>
          <span>
            {design.layout} · {design.duration}s
          </span>
        </div>
        <div className="template-strip">
          <div>
            <p className="eyebrow">A GOOD PLACE TO START</p>
            <h3>Make it feel like your event.</h3>
          </div>
          {templates.map((t) => (
            <button
              className="mini-template"
              key={t.id}
              onClick={() => onChange(structuredClone(t.design))}
            >
              <i style={{ background: t.color }}>
                <span />
                <span />
                <span />
              </i>
              {t.name}
            </button>
          ))}
        </div>
      </div>
      <aside className="design-inspector">
        <div className="inspector-tabs">
          {["canvas", "photos", "layers"].map((t) => (
            <button
              className={tab === t ? "active" : ""}
              onClick={() => setTab(t)}
              key={t}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="inspector-body">
          {tab === "canvas" && (
            <>
              <p className="eyebrow">SCREEN & BACKGROUND</p>
              <div className="fields two">
                {number("Width (px)", "width", 320, 16384)}
                {number("Height (px)", "height", 200, 16384)}
              </div>
              <label>
                Custom aspect ratio (width : height)
                <input
                  placeholder="Example: 5:2 or 9:16"
                  onBlur={(e) => {
                    const parts = e.target.value.split(":").map(Number);
                    if (
                      parts.length === 2 &&
                      parts.every((v) => Number.isFinite(v) && v > 0)
                    ) {
                      const height = Math.round(
                        (design.width * parts[1]) / parts[0],
                      );
                      if (height >= 200 && height <= 16384) patch({ height });
                      else
                        notify(
                          "That ratio needs a different canvas width to stay within 200–16384 pixels high.",
                        );
                    } else if (e.target.value)
                      notify("Enter a ratio such as 5:2.");
                  }}
                />
              </label>
              <small>
                The display preserves this canvas ratio with black bars on
                screens of a different shape.
              </small>
              <label>
                Screen preset
                <select
                  value=""
                  onChange={(e) => {
                    const [width, height] = e.target.value
                      .split("x")
                      .map(Number);
                    patch({ width, height });
                  }}
                >
                  <option value="">Custom resolution</option>
                  <option value="1920x1080">Full HD · 16:9</option>
                  <option value="3840x2160">4K · 16:9</option>
                  <option value="3840x1080">Ultra-wide · 32:9</option>
                  <option value="1080x1920">Portrait · 9:16</option>
                  <option value="1080x1080">Square · 1:1</option>
                </select>
              </label>
              <label>
                Background color
                <div className="color-field">
                  <input
                    type="color"
                    value={design.backgroundColor}
                    onChange={(e) => patch({ backgroundColor: e.target.value })}
                  />
                  <code>{design.backgroundColor}</code>
                </div>
              </label>
              <button
                disabled={busy || !eventId}
                onClick={() => {
                  target.current = "background";
                  file.current!.accept =
                    "image/jpeg,image/png,image/webp,video/mp4,video/webm";
                  file.current?.click();
                }}
              >
                <ImagePlus size={16} /> Upload image / video
              </button>
              {!eventId && <small>Create the event to upload media.</small>}
              {design.backgroundAsset && (
                <button onClick={() => patch({ backgroundAsset: "" })}>
                  Remove background media
                </button>
              )}
              <label>
                Background fit
                <select
                  value={design.backgroundFit ?? "cover"}
                  onChange={(e) =>
                    patch({
                      backgroundFit: e.target.value as "cover" | "contain",
                    })
                  }
                >
                  <option value="cover">Fill canvas (crop)</option>
                  <option value="contain">Fit whole graphic</option>
                </select>
              </label>
              <div className="fields two">
                {number(
                  "Background horizontal offset %",
                  "backgroundX",
                  -100,
                  200,
                )}
                {number(
                  "Background vertical offset %",
                  "backgroundY",
                  -100,
                  200,
                )}
                {number("Background scale %", "backgroundScale", 25, 300)}
                {number("Background blur (px)", "backgroundBlur", 0, 80)}
              </div>
              <label className="check">
                <input
                  type="checkbox"
                  checked={design.showTitle}
                  onChange={(e) => patch({ showTitle: e.target.checked })}
                />{" "}
                Show event title
              </label>
              <hr />
              <p className="eyebrow">REUSABLE TEMPLATES</p>
              <select
                value=""
                onChange={(e) => {
                  const t = customTemplates.find(
                    (t) => t.id === e.target.value,
                  );
                  if (t) onChange(structuredClone(t.design));
                }}
              >
                <option value="">Apply saved template…</option>
                {customTemplates.map((t) => (
                  <option value={t.id} key={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button
                onClick={async () => {
                  const name = prompt("Template name");
                  if (!name) return;
                  try {
                    await save("/admin/templates", { name, design });
                    notify("Template saved. Reopen the editor to load it.");
                  } catch (e) {
                    notify((e as Error).message);
                  }
                }}
              >
                <Save size={16} /> Save as template
              </button>
            </>
          )}
          {tab === "photos" && (
            <>
              <p className="eyebrow">PHOTO PRESENTATION</p>
              <button
                className="cinematic-preset"
                onClick={() =>
                  onChange({
                    ...design,
                    layout: "centerstage",
                    count: 7,
                    duration: 14,
                    transition: "cinematic",
                    transitionMs: 2200,
                    motion: "gentle",
                    fit: "contain",
                    radius: 18,
                    photoX: 3,
                    photoY: 15,
                    photoWidth: 94,
                    photoHeight: 80,
                  })
                }
              >
                <span>Center stage</span>
                <small>Large hero · floating moments · slow cinema</small>
              </button>
              <div className="layout-options">
                {(
                  [
                    "centerstage",
                    "filmstrip",
                    "spotlight",
                    "grid",
                    "collage",
                    "floating",
                  ] as const
                ).map((l) => (
                  <button
                    className={design.layout === l ? "active" : ""}
                    key={l}
                    onClick={() => patch({ layout: l })}
                  >
                    <span className={`layout-icon ${l}`}>▧</span>
                    {l === "centerstage"
                      ? "Center stage"
                      : l === "filmstrip"
                        ? "Editorial"
                        : l}
                  </button>
                ))}
              </div>
              {["centerstage", "filmstrip"].includes(design.layout) && (
                <p className="layout-help">
                  One photo takes center stage; the smaller photos frame it.
                  Each photo gets a turn as the hero. Photo count includes the
                  main image.
                </p>
              )}
              <div className="fields two">
                {number("Photo count", "count", 1, 12)}
                {!["centerstage", "filmstrip", "spotlight"].includes(
                  design.layout,
                ) && number("Columns", "columns", 1, 6)}
                {number("Spacing", "gap", 0, 80)}
                {number("Corners", "radius", 0, 100)}
              </div>
              <label>
                Photo fit
                <select
                  value={design.fit}
                  onChange={(e) =>
                    patch({ fit: e.target.value as Design["fit"] })
                  }
                >
                  <option value="cover">Fill frame (crop)</option>
                  <option value="contain">Entire photo (no crop)</option>
                </select>
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={design.shadow}
                  onChange={(e) => patch({ shadow: e.target.checked })}
                />{" "}
                Photo shadows
              </label>
              <hr />
              <p className="eyebrow">PHOTO AREA · PERCENT</p>
              <div className="fields two">
                {number("Left", "photoX", 0, 95)}
                {number("Top", "photoY", 0, 95)}
                {number("Width", "photoWidth", 5, 100)}
                {number("Height", "photoHeight", 5, 100)}
                {number("Layer order", "photoZ", -10, 100)}
              </div>
              <hr />
              <label>
                Transition
                <select
                  value={design.transition}
                  onChange={(e) =>
                    patch({
                      transition: e.target.value as Design["transition"],
                    })
                  }
                >
                  {[
                    "cinematic",
                    "rise",
                    "crossfade",
                    "fade",
                    "slide",
                    "zoom",
                    "cut",
                  ].map((t) => (
                    <option key={t} value={t}>
                      {t === "cinematic"
                        ? "Cinema dissolve"
                        : t === "rise"
                          ? "Soft rise"
                          : t}
                    </option>
                  ))}
                </select>
              </label>
              <div className="fields two">
                {number("Hold (seconds)", "duration", 2, 120)}
                {number("Transition (ms)", "transitionMs", 0, 8000)}
                {number("Transition motion blur (px)", "motionBlur", 0, 24)}
              </div>
              <p className="layout-help">
                Hold time is added after the transition. New arrivals wait for
                the next scene, so the wall never rushes.
              </p>
              <label>
                Ambient movement
                <select
                  value={design.motion ?? "gentle"}
                  onChange={(e) =>
                    patch({ motion: e.target.value as Design["motion"] })
                  }
                >
                  <option value="gentle">Gentle drift & slow zoom</option>
                  <option value="still">Still photos</option>
                </select>
              </label>
            </>
          )}
          {tab === "layers" && (
            <>
              <p className="eyebrow">GRAPHICS & OVERLAYS</p>
              <div className="button-row">
                <button onClick={() => add("text")}>
                  <Type size={15} /> Text
                </button>
                <button
                  disabled={!eventId || busy}
                  onClick={() => {
                    target.current = "layer";
                    file.current!.accept = "image/png,image/webp";
                    file.current?.click();
                  }}
                >
                  <Plus size={15} /> PNG / logo
                </button>
              </div>
              <div className="layer-list">
                {[...design.layers]
                  .sort((a, b) => b.z - a.z)
                  .map((l) => (
                    <button
                      className={selected === l.id ? "active" : ""}
                      key={l.id}
                      onClick={() => setSelected(l.id)}
                    >
                      <Layers size={14} />
                      {l.type === "text"
                        ? l.text.slice(0, 22) || "Text layer"
                        : "Logo / graphic"}
                      <small>{l.z}</small>
                    </button>
                  ))}
              </div>
              {!layer && (
                <p className="muted">
                  Select a layer to edit its position and appearance.
                </p>
              )}
              {layer && (
                <>
                  <hr />
                  {layer.type === "text" && (
                    <>
                      <label>
                        Text
                        <textarea
                          value={layer.text}
                          onChange={(e) =>
                            patchLayer(layer.id, { text: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Text color
                        <input
                          type="color"
                          value={layer.color}
                          onChange={(e) =>
                            patchLayer(layer.id, { color: e.target.value })
                          }
                        />
                      </label>
                      {layerNumber("Font size", "fontSize", 8, 300)}
                    </>
                  )}
                  <div className="fields two">
                    {layerNumber("Left %", "x", 0, 100, 0.1)}
                    {layerNumber("Top %", "y", 0, 100, 0.1)}
                    {layerNumber("Width %", "width", 1, 100, 0.1)}
                    {layerNumber("Height %", "height", 1, 100, 0.1)}
                    {layerNumber("Rotation", "rotation", -180, 180)}
                    {layerNumber("Opacity", "opacity", 0, 1, 0.05)}
                    {layerNumber("Layer order", "z", -10, 100)}
                  </div>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={layer.visible}
                      onChange={(e) =>
                        patchLayer(layer.id, { visible: e.target.checked })
                      }
                    />{" "}
                    Visible
                  </label>
                  <div className="button-row">
                    <button
                      onClick={() =>
                        patchLayer(layer.id, { z: Math.min(100, layer.z + 1) })
                      }
                    >
                      <ArrowUp size={14} /> Forward
                    </button>
                    <button
                      onClick={() =>
                        patchLayer(layer.id, { z: Math.max(-10, layer.z - 1) })
                      }
                    >
                      <ArrowDown size={14} /> Back
                    </button>
                  </div>
                  <button
                    className="danger"
                    onClick={() => {
                      patch({
                        layers: design.layers.filter((l) => l.id !== layer.id),
                      });
                      setSelected("");
                    }}
                  >
                    <Trash2 size={14} /> Delete layer
                  </button>
                </>
              )}
            </>
          )}
        </div>
        <input
          hidden
          type="file"
          ref={file}
          onChange={(e) => {
            if (e.target.files?.[0]) void uploadAsset(e.target.files[0]);
            e.target.value = "";
          }}
        />
      </aside>
    </div>
  );
}
